import React from 'react';
import { TrendingUp, AlertCircle, AlertTriangle, ChevronUp, ChevronDown, BarChart2, Percent, Target, CheckCircle, XCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета коэффициентов ликвидности
 */
const ResultsOverview = ({ results, industryTypes, formData }) => {
  if (!results) return null;

  // Получаем название выбранной отрасли
  const selectedIndustry = industryTypes.find(i => i.id === formData.settings.industry);
  const industryName = selectedIndustry ? selectedIndustry.name : 'Не указана';

  // Оценка общего состояния ликвидности
  const getOverallLiquidityStatus = () => {
    const { currentRatio, quickRatio, cashRatio } = results;
    const standards = selectedIndustry || { currentRatioMin: 2.0, quickRatioMin: 1.0, cashRatioMin: 0.2 };
    
    // Считаем сколько коэффициентов соответствуют нормативам
    let meetsStandardsCount = 0;
    if (currentRatio >= standards.currentRatioMin) meetsStandardsCount++;
    if (quickRatio >= standards.quickRatioMin) meetsStandardsCount++;
    if (cashRatio >= standards.cashRatioMin) meetsStandardsCount++;
    
    if (meetsStandardsCount === 3) {
      return {
        status: 'excellent',
        label: 'Отличная',
        description: 'Все коэффициенты ликвидности соответствуют или превышают отраслевые нормативы.',
        color: 'green'
      };
    } else if (meetsStandardsCount === 2) {
      return {
        status: 'good',
        label: 'Хорошая',
        description: 'Большинство коэффициентов ликвидности соответствуют отраслевым нормативам.',
        color: 'green'
      };
    } else if (meetsStandardsCount === 1) {
      return {
        status: 'satisfactory',
        label: 'Удовлетворительная',
        description: 'Только один коэффициент ликвидности соответствует отраслевым нормативам, что указывает на возможные проблемы с платежеспособностью.',
        color: 'yellow'
      };
    } else {
      return {
        status: 'poor',
        label: 'Низкая',
        description: 'Ни один из коэффициентов ликвидности не соответствует отраслевым нормативам, что указывает на серьезные проблемы с платежеспособностью.',
        color: 'red'
      };
    }
  };

  const liquidityStatus = getOverallLiquidityStatus();

  // Функция для отображения оценки соответствия нормативу
  const renderAssessmentIcon = (assessment) => {
    if (assessment.status === 'good') {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    } else if (assessment.status === 'warning') {
      return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    } else {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  return (
    <ToolResult title="Результаты анализа ликвидности">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* Коэффициент текущей ликвидности */}
        <div className={`rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border ${results.assessments.currentRatio.status === 'good' ? 'border-blue-100 dark:border-blue-800/50' : 'border-red-100 dark:border-red-800/50'} shadow-sm`}>
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Текущая ликвидность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${results.assessments.currentRatio.status === 'good' ? 'text-blue-800 dark:text-blue-200' : 'text-red-700 dark:text-red-300'}`} aria-live="polite">
              {formatNumber(results.currentRatio, 2)}
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              (норматив: {formatNumber(results.assessments.currentRatio.standard, 2)})
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Отношение оборотных активов к краткосрочным обязательствам
          </p>
        </div>

        {/* Коэффициент быстрой ликвидности */}
        <div className={`rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-5 border ${results.assessments.quickRatio.status === 'good' ? 'border-purple-100 dark:border-purple-800/50' : 'border-red-100 dark:border-red-800/50'} shadow-sm`}>
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <Target className="w-4 h-4"/>
            Быстрая ликвидность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${results.assessments.quickRatio.status === 'good' ? 'text-purple-800 dark:text-purple-200' : 'text-red-700 dark:text-red-300'}`} aria-live="polite">
              {formatNumber(results.quickRatio, 2)}
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              (норматив: {formatNumber(results.assessments.quickRatio.standard, 2)})
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            Отношение высоколиквидных активов к краткосрочным обязательствам
          </p>
        </div>

        {/* Коэффициент абсолютной ликвидности */}
        <div className={`rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border ${results.assessments.cashRatio.status === 'good' ? 'border-emerald-100 dark:border-emerald-800/50' : 'border-red-100 dark:border-red-800/50'} shadow-sm`}>
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Percent className="w-4 h-4"/>
            Абсолютная ликвидность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${results.assessments.cashRatio.status === 'good' ? 'text-emerald-800 dark:text-emerald-200' : 'text-red-700 dark:text-red-300'}`} aria-live="polite">
              {formatNumber(results.cashRatio, 2)}
            </span>
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              (норматив: {formatNumber(results.assessments.cashRatio.standard, 2)})
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Отношение денежных средств к краткосрочным обязательствам
          </p>
        </div>
      </div>

      {/* Общая оценка ликвидности */}
      <div className={`p-4 my-6 rounded-lg border bg-${liquidityStatus.color}-50 dark:bg-${liquidityStatus.color}-900/20 border-${liquidityStatus.color}-200 dark:border-${liquidityStatus.color}-800`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className={`flex-shrink-0 rounded-full p-2 bg-${liquidityStatus.color}-100 dark:bg-${liquidityStatus.color}-900/40`}>
            {liquidityStatus.status === 'excellent' || liquidityStatus.status === 'good' ? (
              <CheckCircle className={`w-6 h-6 text-${liquidityStatus.color}-600 dark:text-${liquidityStatus.color}-400`} />
            ) : (
              <AlertCircle className={`w-6 h-6 text-${liquidityStatus.color}-600 dark:text-${liquidityStatus.color}-400`} />
            )}
          </div>
          <div>
            <h4 className={`text-base font-medium text-${liquidityStatus.color}-800 dark:text-${liquidityStatus.color}-200`}>
              Общая оценка ликвидности: {liquidityStatus.label}
            </h4>
            <p className={`mt-1 text-sm text-${liquidityStatus.color}-700 dark:text-${liquidityStatus.color}-300`}>
              {liquidityStatus.description} <span className="font-medium">Отрасль: {industryName}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-indigo-500" />
          Показатели ликвидности и платежеспособности
        </h3>

        {/* Коэффициент текущей ликвидности */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            {renderAssessmentIcon(results.assessments.currentRatio)}
            <span className="ml-2 font-medium">Коэффициент текущей ликвидности</span>
          </div>
          <div className="flex items-center">
            <span className={`font-semibold ${results.assessments.currentRatio.status === 'good' ? 'text-green-600 dark:text-green-400' : results.assessments.currentRatio.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatNumber(results.currentRatio, 2)}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
              (норматив: {formatNumber(results.assessments.currentRatio.standard, 2)})
            </span>
          </div>
        </div>

        {/* Коэффициент быстрой ликвидности */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            {renderAssessmentIcon(results.assessments.quickRatio)}
            <span className="ml-2 font-medium">Коэффициент быстрой ликвидности</span>
          </div>
          <div className="flex items-center">
            <span className={`font-semibold ${results.assessments.quickRatio.status === 'good' ? 'text-green-600 dark:text-green-400' : results.assessments.quickRatio.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatNumber(results.quickRatio, 2)}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
              (норматив: {formatNumber(results.assessments.quickRatio.standard, 2)})
            </span>
          </div>
        </div>

        {/* Коэффициент абсолютной ликвидности */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            {renderAssessmentIcon(results.assessments.cashRatio)}
            <span className="ml-2 font-medium">Коэффициент абсолютной ликвидности</span>
          </div>
          <div className="flex items-center">
            <span className={`font-semibold ${results.assessments.cashRatio.status === 'good' ? 'text-green-600 dark:text-green-400' : results.assessments.cashRatio.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatNumber(results.cashRatio, 2)}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
              (норматив: {formatNumber(results.assessments.cashRatio.standard, 2)})
            </span>
          </div>
        </div>

        {/* Коэффициент общей платежеспособности */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            {results.generalSolvencyRatio >= 1 ? 
              <CheckCircle className="w-5 h-5 text-green-500" /> : 
              <XCircle className="w-5 h-5 text-red-500" />
            }
            <span className="ml-2 font-medium">Коэффициент общей платежеспособности</span>
          </div>
          <div className="flex items-center">
            <span className={`font-semibold ${results.generalSolvencyRatio >= 1 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatNumber(results.generalSolvencyRatio, 2)}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
              (норматив: ≥ 1,00)
            </span>
          </div>
        </div>

        {/* Коэффициент обеспеченности собственными оборотными средствами */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            {results.workingCapitalRatio >= 0.1 ? 
              <CheckCircle className="w-5 h-5 text-green-500" /> : 
              <XCircle className="w-5 h-5 text-red-500" />
            }
            <span className="ml-2 font-medium">Коэффициент обеспеч. собств. оборотными средствами</span>
          </div>
          <div className="flex items-center">
            <span className={`font-semibold ${results.workingCapitalRatio >= 0.1 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatNumber(results.workingCapitalRatio, 2)}
            </span>
            <span className="text-gray-500 dark:text-gray-400 ml-2 text-sm">
              (норматив: ≥ 0,10)
            </span>
          </div>
        </div>

        {/* Доля оборотных средств в активах */}
        <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex items-center">
            <span className="ml-2 font-medium">Доля оборотных средств в активах</span>
          </div>
          <div className="flex items-center">
            <span className="font-semibold text-gray-800 dark:text-gray-200">
              {formatNumber(results.currentAssetsToTotalAssetsRatio * 100, 1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Анализ ликвидности баланса */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-indigo-500" />
          Анализ ликвидности баланса
        </h3>

        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Условие
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Активы
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Пассивы
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Статус
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  A1 ≥ П1
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.a1, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.l1, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${results.balanceLiquidity.balanceCondition1 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {results.balanceLiquidity.balanceCondition1 ? 'Выполняется' : 'Не выполняется'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  A2 ≥ П2
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.a2, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.l2, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${results.balanceLiquidity.balanceCondition2 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {results.balanceLiquidity.balanceCondition2 ? 'Выполняется' : 'Не выполняется'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  A3 ≥ П3
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.a3, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.l3, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${results.balanceLiquidity.balanceCondition3 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {results.balanceLiquidity.balanceCondition3 ? 'Выполняется' : 'Не выполняется'}
                  </span>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                  A4 ≤ П4
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.a4, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                  {formatNumber(results.balanceLiquidity.l4, 0)} ₽
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${results.balanceLiquidity.balanceCondition4 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'}`}>
                    {results.balanceLiquidity.balanceCondition4 ? 'Выполняется' : 'Не выполняется'}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Ликвидность баланса: </span>
            {results.balanceLiquidity.absoluteLiquidity ? (
              <span className="text-green-600 dark:text-green-400 font-medium">Абсолютная</span>
            ) : (
              <span className="text-yellow-600 dark:text-yellow-400 font-medium">Недостаточная</span>
            )}
          </p>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            A1 - наиболее ликвидные активы (денежные средства и краткосрочные финансовые вложения)<br />
            A2 - быстрореализуемые активы (дебиторская задолженность)<br />
            A3 - медленно реализуемые активы (запасы, НДС, прочие оборотные активы)<br />
            A4 - труднореализуемые активы (внеоборотные активы)<br />
            П1 - наиболее срочные обязательства (кредиторская задолженность, задолженность по налогам)<br />
            П2 - краткосрочные пассивы (краткосрочные займы)<br />
            П3 - долгосрочные пассивы (долгосрочные обязательства)<br />
            П4 - постоянные пассивы (собственный капитал)
          </p>
        </div>
      </div>

      {/* Оценка риска банкротства */}
      {formData.settings.includeBankruptcyRiskAssessment && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-indigo-500" />
            Оценка риска банкротства
          </h3>

          <div className={`p-4 rounded-lg border ${
            results.bankruptcyRisk.level === 'low' 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
              : results.bankruptcyRisk.level === 'medium'
                ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className={`flex-shrink-0 rounded-full p-2 ${
                results.bankruptcyRisk.level === 'low' 
                  ? 'bg-green-100 dark:bg-green-900/40' 
                  : results.bankruptcyRisk.level === 'medium'
                    ? 'bg-yellow-100 dark:bg-yellow-900/40'
                    : 'bg-red-100 dark:bg-red-900/40'
              }`}>
                {results.bankruptcyRisk.level === 'low' ? (
                  <CheckCircle className={`w-6 h-6 text-green-600 dark:text-green-400`} />
                ) : results.bankruptcyRisk.level === 'medium' ? (
                  <AlertTriangle className={`w-6 h-6 text-yellow-600 dark:text-yellow-400`} />
                ) : (
                  <AlertCircle className={`w-6 h-6 text-red-600 dark:text-red-400`} />
                )}
              </div>
              <div>
                <h4 className={`text-base font-medium ${
                  results.bankruptcyRisk.level === 'low' 
                    ? 'text-green-800 dark:text-green-200' 
                    : results.bankruptcyRisk.level === 'medium'
                      ? 'text-yellow-800 dark:text-yellow-200'
                      : 'text-red-800 dark:text-red-200'
                }`}>
                  Z-score: {formatNumber(results.zScore, 2)}
                </h4>
                <p className={`mt-1 text-sm ${
                  results.bankruptcyRisk.level === 'low' 
                    ? 'text-green-700 dark:text-green-300' 
                    : results.bankruptcyRisk.level === 'medium'
                      ? 'text-yellow-700 dark:text-yellow-300'
                      : 'text-red-700 dark:text-red-300'
                }`}>
                  {results.bankruptcyRisk.description}
                </p>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  Расчет выполнен по модели Альтмана, адаптированной для российских компаний. Модель учитывает структуру капитала, рентабельность и ликвидность предприятия.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Рекомендации */}
      {results.recommendations && results.recommendations.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-indigo-500" />
            Рекомендации по улучшению ликвидности
          </h3>

          <div className="space-y-2">
            {results.recommendations.map((rec, index) => (
              <div key={index} className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
                <p className="text-sm text-indigo-800 dark:text-indigo-200">
                  {rec.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </ToolResult>
  );
};

export default ResultsOverview;